import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, ChevronRight, Activity, Monitor, Smartphone, Globe, X, Search,
  Phone, Hash, Clock, MapPin, Layers, Mail, Zap,
} from 'lucide-react';
import { EngagementTabs, StatCard, CopyableId } from '../components/engagement/primitives';
import {
  fetchApplicationEvents,
} from '../services/engagementService';

// Raw events.documents.Event rows — every event any application ingested
// via POST /api/v1/events/ (page views, quota checks, slot lookups,
// conversions, ...), NOT the CEP reminder ledger (that's Engagement Logs).
const PLATFORM_ICON = {
  web: Globe,
  mobile: Smartphone,
  server: Monitor,
};

const KPI_ACCENTS = {
  total: { bg: 'bg-blue-50', fg: 'text-blue-600', bar: 'bg-blue-500' },
  web: { bg: 'bg-indigo-50', fg: 'text-indigo-600', bar: 'bg-indigo-500' },
  mobile: { bg: 'bg-purple-50', fg: 'text-purple-600', bar: 'bg-purple-500' },
  server: { bg: 'bg-slate-50', fg: 'text-slate-600', bar: 'bg-slate-400' },
};

const PAGE_SIZE = 25;

// Force IST regardless of the admin's own machine/browser timezone —
// toLocaleString() with no options renders in the VIEWER's local tz, which
// silently shows UTC (or anything else) for anyone not already set to IST,
// even though the backend timestamps are correctly UTC-stamped instants.
const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'medium' })} IST`;
};

const PlatformIcon = ({ platform }) => {
  const Icon = PLATFORM_ICON[platform] || Activity;
  return <Icon className="w-3.5 h-3.5 text-gray-400" />;
};

// Flattens a nested object into [{ path, value }] rows for tabular display —
// event.data is an arbitrary JSON blob from whichever application sent the
// event, so it can nest a few levels (see events/api/views_events/serializers.py:
// e.g. { event_properties: {...}, page: {...}, device: {...} }).
const flattenEntries = (obj, prefix = '') => {
  const rows = [];
  if (obj === null || obj === undefined) return rows;
  Object.entries(obj).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      rows.push(...flattenEntries(value, path));
    } else if (Array.isArray(value)) {
      rows.push({ path, value: value.length ? value.join(', ') : '—' });
    } else {
      rows.push({ path, value: value === null || value === undefined || value === '' ? '—' : String(value) });
    }
  });
  return rows;
};

const InfoChip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
    <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {children}
  </span>
);

const GROUP_STYLE = {
  event_properties: { icon: Layers, label: 'Event Properties', color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-100' },
  page: { icon: MapPin, label: 'Page', color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-100' },
  device: { icon: Smartphone, label: 'Device', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
};
const DEFAULT_GROUP_STYLE = { icon: Layers, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };

// Groups event.data's top-level keys into their own labeled, colored cards
// (event_properties/page/device/...) with a plain key: value row list —
// easier to scan for exact values than pill-chips, and each group's color
// gives the eye something other than gray to anchor on.
const DataGroup = ({ groupKey, entries }) => {
  const style = GROUP_STYLE[groupKey] || DEFAULT_GROUP_STYLE;
  const Icon = style.icon;
  return (
    <div className={`border rounded-xl overflow-hidden ${style.border}`}>
      <div className={`flex items-center gap-2 px-3.5 py-2 ${style.bg}`}>
        <Icon className={`w-4 h-4 ${style.color}`} />
        <p className={`text-xs font-semibold uppercase tracking-wider ${style.color}`}>{style.label || groupKey}</p>
      </div>
      <div className="bg-white divide-y divide-gray-100">
        {entries.map(({ path, value }) => (
          <div key={path} className="flex items-center justify-between gap-4 px-3.5 py-2 text-sm">
            <span className="text-gray-500 font-mono text-xs">{path}</span>
            <span className="text-gray-900 font-medium"><CopyableId value={value} /></span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EventDetailModal = ({ event, onClose }) => {
  if (!event) return null;
  const userName = event.user?.full_name || [event.user?.first_name, event.user?.last_name].filter(Boolean).join(' ');

  // Split event.data into its top-level groups (event_properties/page/device/...)
  // rather than one flattened list — each becomes its own DataGroup card.
  const dataGroups = Object.entries(event.data || {}).map(([key, value]) => ({
    key,
    entries: value !== null && typeof value === 'object' && !Array.isArray(value)
      ? flattenEntries(value)
      : flattenEntries({ [key]: value }),
  })).filter((g) => g.entries.length > 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        {/* Header — white, matching Engagement Logs' modal headers */}
        <div className="relative bg-white border-b border-gray-100 px-6 py-5 rounded-t-xl">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-3 min-w-0 pr-8">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 flex items-center justify-center font-semibold text-lg shrink-0">
              {(userName || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{userName || 'Unknown user'}</h3>
              <div className="flex items-center gap-3 flex-wrap mt-0.5">
                {event.user?.mobile_no && <InfoChip icon={Phone}>{event.user.mobile_no}</InfoChip>}
                {event.user?.email && <InfoChip icon={Mail}>{event.user.email}</InfoChip>}
                {event.user?.ref_code && <InfoChip icon={Hash}>{event.user.ref_code}</InfoChip>}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Event — name front and center, platform/time/anchor as a strip */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-600 text-white shrink-0">
                <Zap className="w-3.5 h-3.5" />
              </span>
              <p className="text-sm font-bold text-gray-900">{event.event_name || '—'}</p>
            </div>
            {event.event_description && (
              <p className="text-xs text-gray-600 mb-3 pl-9">{event.event_description}</p>
            )}
            <div className="flex items-center gap-4 flex-wrap text-xs text-gray-600 pl-9">
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-gray-500" /> {fmt(event.created_at)}</span>
              {event.anchor && <span className="inline-flex items-center gap-1"><Hash className="w-3.5 h-3.5 text-gray-500" /> {event.anchor}</span>}
              <span className="inline-flex items-center gap-1 capitalize">
                <PlatformIcon platform={event.platform} /> {event.platform || 'unknown platform'} · {event.source || 'unknown source'}
              </span>
            </div>
          </div>

          {/* Application ID — its own callout, not a footnote, so it's
              easy to spot and copy without hunting through the event card. */}
          <div className="flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
            <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Application ID</span>
            <div className="text-indigo-900">
              <CopyableId value={event.application_id} displayLength={36} />
            </div>
          </div>

          {/* data payload — grouped into colored cards by top-level key */}
          {dataGroups.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Captured Data</h4>
              <div className="space-y-3">
                {dataGroups.map((g) => (
                  <DataGroup key={g.key} groupKey={g.key} entries={g.entries} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ApplicationEvents = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [eventNameFilter, setEventNameFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  // KPI card counts — aggregated server-side across the FULL filtered
  // result set (see events/api/views_events/list.py's item_frequencies),
  // not just the current page's rows.
  const [platformCounts, setPlatformCounts] = useState({});

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');

    const res = await fetchApplicationEvents({
      event_name: eventNameFilter || undefined,
      search: search || undefined,
      page,
      items: PAGE_SIZE,
    });

    if (res?.metadata?.api_status) {
      setRows(res.msg?.items || []);
      setCount(res.msg?.pagination?.total || 0);
      setPlatformCounts(res.msg?.platform_counts || {});
    } else {
      setRows([]);
      setCount(0);
      setPlatformCounts({});
      setError(typeof res?.msg === 'string' ? res.msg : 'Failed to load application events');
    }
    setLoading(false);
  }, [page, eventNameFilter, search]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const onFilter = (setter) => (e) => { setter(e.target.value); setPage(1); };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/subscriber-wallet')}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all"
              aria-label="Back to home">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/subscriber-wallet/engagement')}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all"
              aria-label="Back to Customer Engagement">
              <ChevronRight className="w-4 h-4 rotate-180" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">Application Events</h1>
              <p className="text-sm text-gray-400">Raw events reported by applications via the events API</p>
            </div>
          </div>
          <button
            onClick={loadRows}
            className="self-start sm:self-auto px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 shadow-sm flex items-center gap-2 transition-all">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Module tabs */}
        <EngagementTabs active="events" />

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard index={0} icon={Activity} label="Total Events" accent={KPI_ACCENTS.total} loading={loading} value={count} />
          <StatCard index={1} icon={Globe} label="Web" accent={KPI_ACCENTS.web} loading={loading} value={platformCounts.web ?? 0} />
          <StatCard index={2} icon={Smartphone} label="Mobile" accent={KPI_ACCENTS.mobile} loading={loading} value={platformCounts.mobile ?? 0} />
          <StatCard index={3} icon={Monitor} label="Desktop" accent={KPI_ACCENTS.server} loading={loading} value={platformCounts.server ?? 0} />
        </div>

        {/* Filter bar — one search box matches Application ID, Ref Code, or
            Mobile Number (whichever the operator has), plus a separate
            event-name filter since that's a distinct axis. */}
        <div className="sticky top-4 z-20 bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="relative flex-1 min-w-[16rem] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={onFilter(setSearch)}
                placeholder="Search by Application ID, Ref Code, or Mobile Number"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">Event name</span>
              <input
                value={eventNameFilter}
                onChange={onFilter(setEventNameFilter)}
                placeholder="e.g. vc_slots_loaded"
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent w-56"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="px-4 py-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl">{error}</div>
        )}

        {/* Table — "Occurred At" leads, matching EngagementMessageLogs.jsx's
            "Last Attempt"-first column order convention. */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {['Occurred At', 'Event', 'User', 'Application ID', 'Anchor', 'Platform'].map((h, i) => (
                    <th key={i} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-16 text-center">
                      {loading ? (
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                          <RefreshCw className="w-6 h-6 animate-spin" /><span className="text-sm">Loading events…</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3 text-gray-400">
                          <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                            <Activity className="w-6 h-6 text-gray-300" />
                          </div>
                          <p className="text-sm font-medium text-gray-500">No events for the selected filters</p>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  rows.map((ev) => (
                    <tr key={ev.id} onClick={() => setSelectedEvent(ev)}
                      className="hover:bg-blue-50/40 cursor-pointer transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{fmt(ev.created_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">{ev.event_name || '—'}</p>
                        {ev.event_description && (
                          <p className="text-xs text-gray-400 max-w-xs truncate">{ev.event_description}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {(() => {
                          const name = ev.user?.full_name || [ev.user?.first_name, ev.user?.last_name].filter(Boolean).join(' ');
                          return (
                            <>
                              <p className="text-gray-800 font-medium">{name || '—'}</p>
                              {ev.user?.mobile_no && <p className="text-xs text-gray-400">{ev.user.mobile_no}</p>}
                              {ev.user?.ref_code && <p className="text-xs text-gray-400">{ev.user.ref_code}</p>}
                            </>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 hover:text-gray-800">
                        <CopyableId value={ev.application_id} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ev.anchor || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                          <PlatformIcon platform={ev.platform} /> {ev.platform || '—'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && count > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, count)} of {count}
              </div>
              <div className="flex items-center gap-2">
                <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${page === 1 ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}>Prev</button>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${page >= totalPages ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
};

export default ApplicationEvents;
